/**
 * Zodiac AI Capability Registry - Enhanced Intelligence System
 * 
 * UPGRADE: From structured catalog to decision intelligence system
 * 
 * New Features:
 * - Scoring Model (Reliability, Workforce Fit, Integration Complexity, AI Readiness)
 * - Structured Use Cases per lab
 * - Tool Stacks (curated combinations)
 * - Opportunity alignment data
 * 
 * This is NOT a marketplace or directory - it's an intelligence system.
 */

// ============================================
// ENUMS & CONSTANTS
// ============================================

export const AI_CATEGORIES = [
  { id: "content-writing", name: "Content & Writing", icon: "BookOpen" },
  { id: "data-analytics", name: "Data & Analytics", icon: "Database" },
  { id: "automation-workflow", name: "Automation & Workflow", icon: "Zap" },
  { id: "communication", name: "Communication & Collaboration", icon: "MessageSquare" },
  { id: "research-learning", name: "Research & Learning", icon: "Search" },
  { id: "creative-design", name: "Creative & Design", icon: "Palette" },
  { id: "coding-development", name: "Coding & Development", icon: "Code" },
  { id: "project-management", name: "Project Management", icon: "Kanban" }
];

export const AI_FUNCTIONS = [
  "Text Generation",
  "Text Analysis",
  "Image Generation",
  "Image Analysis",
  "Data Analysis",
  "Automation",
  "Coding Assistance",
  "Translation",
  "Transcription",
  "Research",
  "Summarization",
  "Presentation",
  "Document Processing",
  "Customer Support",
  "Project Planning"
];

export const INDUSTRIES = [
  "All Industries",
  "Healthcare",
  "Finance",
  "Education",
  "Manufacturing",
  "Technology",
  "Logistics",
  "Construction",
  "Government",
  "Nonprofit",
  "Retail"
];

export const EVALUATION_STATUS = {
  VERIFIED: "verified",
  UNDER_REVIEW: "under_review",
  PENDING: "pending"
};

// Integration Complexity Levels
export const INTEGRATION_COMPLEXITY = {
  LOW: "low",        // Browser-based, no setup
  MEDIUM: "medium",  // API integration, some configuration
  HIGH: "high"       // Enterprise deployment, custom integration
};

// AI Readiness Levels (user skill required)
export const AI_READINESS_LEVEL = {
  BASIC: "basic",           // No AI experience needed
  INTERMEDIATE: "intermediate", // Some AI familiarity helpful
  ADVANCED: "advanced"      // Requires AI expertise
};

// ============================================
// ENHANCED AI REGISTRY
// ============================================

export const AI_REGISTRY = [
  // ============================================
  // FULLY ENHANCED TOOL EXAMPLE: ChatGPT-4
  // ============================================
  {
    id: "chatgpt-4",
    name: "ChatGPT-4",
    provider: "OpenAI",
    description: "Advanced language model for text generation, analysis, coding assistance, and conversational AI. Industry-leading natural language processing with broad application across workforce development contexts.",
    category: "content-writing",
    functions: ["Text Generation", "Text Analysis", "Coding Assistance", "Research"],
    industries: ["All Industries"],
    
    // ENHANCED: Scoring Model
    scores: {
      reliability: 92,           // 1-100: Consistency, uptime, accuracy
      workforceFit: 95,          // 1-100: Alignment with workforce development
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    
    // ENHANCED: Lab Relevance with Structured Use Cases
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 95,
        useCases: [
          {
            title: "Resume & Cover Letter Optimization",
            description: "Generate tailored resumes and cover letters based on job descriptions and career goals",
            outcomes: ["Higher application success rate", "Professional positioning", "Skill highlighting"]
          },
          {
            title: "Interview Preparation",
            description: "Practice interview responses, receive feedback, and prepare for specific roles",
            outcomes: ["Increased confidence", "Better articulation", "Role-specific readiness"]
          },
          {
            title: "Skill Gap Analysis",
            description: "Identify skill gaps by comparing current capabilities with target role requirements",
            outcomes: ["Clear development path", "Prioritized learning", "Career alignment"]
          },
          {
            title: "Learning Acceleration",
            description: "Get explanations of complex concepts, generate study materials, and test understanding",
            outcomes: ["Faster comprehension", "Personalized learning", "Knowledge retention"]
          }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 88,
        useCases: [
          {
            title: "Policy & Documentation Drafting",
            description: "Generate initial drafts of policies, procedures, and organizational documents",
            outcomes: ["Faster documentation", "Consistent formatting", "Compliance alignment"]
          },
          {
            title: "Meeting Preparation & Synthesis",
            description: "Prepare agendas, summarize meetings, and generate action items",
            outcomes: ["Meeting efficiency", "Clear accountability", "Better follow-through"]
          },
          {
            title: "Training Content Development",
            description: "Create training materials, assessments, and learning resources",
            outcomes: ["Scalable training", "Consistent messaging", "Reduced development time"]
          }
        ]
      },
      institutional: {
        relevant: true,
        fitScore: 82,
        useCases: [
          {
            title: "Research Synthesis",
            description: "Synthesize findings from multiple research sources into coherent summaries",
            outcomes: ["Faster literature review", "Cross-source insights", "Evidence aggregation"]
          },
          {
            title: "Policy Analysis Support",
            description: "Analyze policy implications and generate comparative assessments",
            outcomes: ["Informed decision-making", "Stakeholder communication", "Impact assessment"]
          }
        ]
      }
    },
    
    // Evaluation metadata
    evaluation: {
      status: EVALUATION_STATUS.VERIFIED,
      reliabilityScore: 92,
      integrationReadiness: 95,
      lastEvaluated: "2026-03-15",
      evaluationNotes: "Industry leader with consistent performance. Excellent for workforce development applications."
    },
    
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization", "institutional"],
    website: "https://openai.com/chatgpt",
    pricing: "Freemium",
    
    // Opportunity alignment (for Opportunity Intelligence Engine)
    opportunityAlignment: {
      businessOpportunities: ["AI Consulting", "Content Creation Services", "Training Development"],
      servicePathways: ["AI Implementation Consultant", "Content Strategist", "Learning Designer"],
      careerPathways: ["AI Operations Specialist", "Technical Writer", "L&D Professional"]
    }
  },

  // ============================================
  // CONTENT & WRITING
  // ============================================
  {
    id: "claude-3",
    name: "Claude 3",
    provider: "Anthropic",
    description: "AI assistant focused on helpful, harmless, and honest responses with strong reasoning capabilities.",
    category: "content-writing",
    functions: ["Text Generation", "Text Analysis", "Research", "Summarization"],
    industries: ["All Industries"],
    scores: {
      reliability: 90,
      workforceFit: 93,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 93,
        useCases: [
          { title: "Writing Assistance", description: "Improve professional communication and documentation", outcomes: ["Clearer writing", "Professional tone"] },
          { title: "Learning Support", description: "Get explanations and guidance on complex topics", outcomes: ["Better understanding", "Self-paced learning"] },
          { title: "Problem-Solving Guidance", description: "Work through challenges with structured thinking support", outcomes: ["Better decisions", "Clear reasoning"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 88,
        useCases: [
          { title: "Document Analysis", description: "Review and analyze business documents for insights", outcomes: ["Faster review", "Key insight extraction"] },
          { title: "Strategic Planning Support", description: "Develop strategic documents and plans", outcomes: ["Structured thinking", "Comprehensive planning"] }
        ]
      },
      institutional: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Research Assistance", description: "Support academic and policy research workflows", outcomes: ["Research efficiency", "Quality synthesis"] },
          { title: "Complex Analysis", description: "Analyze multi-faceted issues with nuanced reasoning", outcomes: ["Deeper insights", "Balanced perspectives"] }
        ]
      }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 90, integrationReadiness: 92, lastEvaluated: "2026-03-10" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization", "institutional"],
    website: "https://anthropic.com/claude",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["AI Consulting", "Research Services"],
      servicePathways: ["AI Analyst", "Research Consultant"],
      careerPathways: ["AI Specialist", "Research Analyst"]
    }
  },
  {
    id: "jasper-ai",
    name: "Jasper AI",
    provider: "Jasper",
    description: "AI content platform for marketing teams to create on-brand content at scale.",
    category: "content-writing",
    functions: ["Text Generation", "Summarization"],
    industries: ["All Industries"],
    scores: {
      reliability: 85,
      workforceFit: 78,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 78,
        useCases: [
          { title: "Personal Branding Content", description: "Create LinkedIn posts and professional content", outcomes: ["Visibility", "Thought leadership"] },
          { title: "Portfolio Descriptions", description: "Write compelling descriptions for portfolio items", outcomes: ["Professional presentation", "Clear value proposition"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Marketing Content", description: "Generate marketing copy and campaigns", outcomes: ["Content velocity", "Brand consistency"] },
          { title: "Brand Messaging", description: "Maintain consistent brand voice across content", outcomes: ["Brand alignment", "Message clarity"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 85, integrationReadiness: 88, lastEvaluated: "2026-02-15" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://jasper.ai",
    pricing: "Paid",
    opportunityAlignment: {
      businessOpportunities: ["Content Agency", "Marketing Services"],
      servicePathways: ["Content Strategist", "Marketing Consultant"],
      careerPathways: ["Content Manager", "Marketing Specialist"]
    }
  },
  {
    id: "grammarly",
    name: "Grammarly",
    provider: "Grammarly Inc.",
    description: "AI-powered writing assistant for grammar, clarity, and tone optimization.",
    category: "content-writing",
    functions: ["Text Analysis", "Text Generation"],
    industries: ["All Industries"],
    scores: {
      reliability: 94,
      workforceFit: 88,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 92,
        useCases: [
          { title: "Professional Communication", description: "Polish emails and business communication", outcomes: ["Professional image", "Clear messaging"] },
          { title: "Resume Polishing", description: "Ensure error-free, professional resume", outcomes: ["First impression", "Attention to detail"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Team Communication Standards", description: "Maintain consistent writing quality across teams", outcomes: ["Professional standards", "Reduced errors"] },
          { title: "Document Quality Control", description: "Ensure document accuracy before publishing", outcomes: ["Quality assurance", "Brand protection"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 94, integrationReadiness: 96, lastEvaluated: "2026-02-20" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://grammarly.com",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Writing Services", "Editing Consultancy"],
      servicePathways: ["Editor", "Communication Coach"],
      careerPathways: ["Communications Specialist", "Technical Writer"]
    }
  },

  // ============================================
  // DATA & ANALYTICS
  // ============================================
  {
    id: "tableau-ai",
    name: "Tableau AI",
    provider: "Salesforce",
    description: "AI-powered data visualization and analytics platform with natural language queries.",
    category: "data-analytics",
    functions: ["Data Analysis", "Summarization"],
    industries: ["All Industries"],
    scores: {
      reliability: 91,
      workforceFit: 75,
      integrationComplexity: INTEGRATION_COMPLEXITY.MEDIUM,
      aiReadinessLevel: AI_READINESS_LEVEL.INTERMEDIATE
    },
    labRelevance: {
      workforce: { relevant: false, fitScore: 0, useCases: [] },
      organization: {
        relevant: true,
        fitScore: 92,
        useCases: [
          { title: "Workforce Analytics", description: "Visualize workforce metrics and trends", outcomes: ["Data-driven decisions", "Trend identification"] },
          { title: "Performance Dashboards", description: "Create interactive performance tracking", outcomes: ["Real-time insights", "Goal tracking"] }
        ]
      },
      institutional: {
        relevant: true,
        fitScore: 88,
        useCases: [
          { title: "Sector Benchmarking", description: "Compare metrics across institutions", outcomes: ["Competitive positioning", "Best practice identification"] },
          { title: "Policy Impact Analysis", description: "Visualize policy outcomes", outcomes: ["Evidence-based policy", "Stakeholder communication"] }
        ]
      }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 91, integrationReadiness: 85, lastEvaluated: "2026-02-28" },
    zodiacStandard: true,
    recommendedFor: ["organization", "institutional"],
    website: "https://tableau.com",
    pricing: "Paid",
    opportunityAlignment: {
      businessOpportunities: ["Analytics Consulting", "BI Implementation"],
      servicePathways: ["Data Visualization Consultant", "Analytics Specialist"],
      careerPathways: ["Data Analyst", "BI Developer"]
    }
  },
  {
    id: "power-bi-copilot",
    name: "Power BI Copilot",
    provider: "Microsoft",
    description: "AI-assisted business intelligence with natural language report generation.",
    category: "data-analytics",
    functions: ["Data Analysis", "Summarization", "Text Generation"],
    industries: ["All Industries"],
    scores: {
      reliability: 89,
      workforceFit: 72,
      integrationComplexity: INTEGRATION_COMPLEXITY.MEDIUM,
      aiReadinessLevel: AI_READINESS_LEVEL.INTERMEDIATE
    },
    labRelevance: {
      workforce: { relevant: false, fitScore: 0, useCases: [] },
      organization: {
        relevant: true,
        fitScore: 90,
        useCases: [
          { title: "Executive Dashboards", description: "Generate executive-level reports automatically", outcomes: ["Leadership visibility", "Quick insights"] },
          { title: "Resource Analysis", description: "Analyze resource allocation and utilization", outcomes: ["Optimization", "Cost management"] }
        ]
      },
      institutional: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Cross-Organization Comparison", description: "Compare metrics across multiple organizations", outcomes: ["Benchmarking", "Best practices"] },
          { title: "Compliance Reporting", description: "Generate compliance and regulatory reports", outcomes: ["Audit readiness", "Risk management"] }
        ]
      }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 89, integrationReadiness: 88, lastEvaluated: "2026-03-01" },
    zodiacStandard: true,
    recommendedFor: ["organization", "institutional"],
    website: "https://powerbi.microsoft.com",
    pricing: "Paid",
    opportunityAlignment: {
      businessOpportunities: ["BI Consulting", "Microsoft Implementation"],
      servicePathways: ["Power BI Consultant", "Data Strategist"],
      careerPathways: ["Business Intelligence Analyst", "Data Engineer"]
    }
  },
  {
    id: "julius-ai",
    name: "Julius AI",
    provider: "Julius",
    description: "AI data analyst that processes spreadsheets and generates insights through conversation.",
    category: "data-analytics",
    functions: ["Data Analysis", "Text Generation", "Summarization"],
    industries: ["All Industries"],
    scores: {
      reliability: 78,
      workforceFit: 82,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 80,
        useCases: [
          { title: "Personal Data Analysis", description: "Analyze personal career or financial data", outcomes: ["Self-awareness", "Data literacy"] },
          { title: "Skills Gap Analysis", description: "Quantify skill gaps from assessment data", outcomes: ["Priority identification", "Development planning"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 75,
        useCases: [
          { title: "Quick Data Exploration", description: "Rapidly explore datasets through conversation", outcomes: ["Fast insights", "Hypothesis testing"] },
          { title: "Ad-hoc Analysis", description: "Answer one-off data questions quickly", outcomes: ["Agile decision-making", "Reduced analyst load"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.UNDER_REVIEW, reliabilityScore: 78, integrationReadiness: 82, lastEvaluated: "2026-02-15" },
    zodiacStandard: false,
    recommendedFor: ["workforce", "organization"],
    website: "https://julius.ai",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Data Services", "Analytics Training"],
      servicePathways: ["Data Literacy Coach", "Analytics Trainer"],
      careerPathways: ["Junior Data Analyst", "Business Analyst"]
    }
  },

  // ============================================
  // AUTOMATION & WORKFLOW
  // ============================================
  {
    id: "zapier-ai",
    name: "Zapier AI",
    provider: "Zapier",
    description: "AI-powered workflow automation connecting thousands of apps with natural language setup.",
    category: "automation-workflow",
    functions: ["Automation"],
    industries: ["All Industries"],
    scores: {
      reliability: 93,
      workforceFit: 85,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Personal Productivity Automation", description: "Automate repetitive personal tasks", outcomes: ["Time savings", "Consistency"] },
          { title: "Job Application Tracking", description: "Automate application tracking and follow-ups", outcomes: ["Organization", "Follow-through"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 92,
        useCases: [
          { title: "HR Process Automation", description: "Automate onboarding and HR workflows", outcomes: ["Efficiency", "Consistency"] },
          { title: "Reporting Automation", description: "Automate regular report generation", outcomes: ["Time savings", "Accuracy"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 93, integrationReadiness: 95, lastEvaluated: "2026-02-20" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://zapier.com",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Automation Consulting", "Process Optimization"],
      servicePathways: ["Automation Specialist", "Process Consultant"],
      careerPathways: ["Operations Analyst", "Automation Engineer"]
    }
  },
  {
    id: "make-ai",
    name: "Make (Integromat)",
    provider: "Make",
    description: "Visual workflow automation platform with AI-assisted scenario building.",
    category: "automation-workflow",
    functions: ["Automation"],
    industries: ["All Industries"],
    scores: {
      reliability: 88,
      workforceFit: 80,
      integrationComplexity: INTEGRATION_COMPLEXITY.MEDIUM,
      aiReadinessLevel: AI_READINESS_LEVEL.INTERMEDIATE
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 75,
        useCases: [
          { title: "Personal Task Automation", description: "Build custom automation workflows", outcomes: ["Efficiency", "Technical skills"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 88,
        useCases: [
          { title: "Complex Workflow Automation", description: "Build sophisticated multi-step workflows", outcomes: ["Process efficiency", "Error reduction"] },
          { title: "Multi-System Integration", description: "Connect disparate systems seamlessly", outcomes: ["Data flow", "System unity"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 88, integrationReadiness: 85, lastEvaluated: "2026-02-25" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://make.com",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Integration Services", "Workflow Consulting"],
      servicePathways: ["Integration Specialist", "Workflow Designer"],
      careerPathways: ["Solutions Architect", "Integration Developer"]
    }
  },
  {
    id: "uipath",
    name: "UiPath",
    provider: "UiPath",
    description: "Enterprise robotic process automation (RPA) platform with AI capabilities.",
    category: "automation-workflow",
    functions: ["Automation", "Document Processing"],
    industries: ["All Industries"],
    scores: {
      reliability: 94,
      workforceFit: 68,
      integrationComplexity: INTEGRATION_COMPLEXITY.HIGH,
      aiReadinessLevel: AI_READINESS_LEVEL.ADVANCED
    },
    labRelevance: {
      workforce: { relevant: false, fitScore: 0, useCases: [] },
      organization: {
        relevant: true,
        fitScore: 90,
        useCases: [
          { title: "Enterprise Process Automation", description: "Automate complex enterprise processes", outcomes: ["Scale efficiency", "Cost reduction"] },
          { title: "Document Processing", description: "Extract and process document data automatically", outcomes: ["Accuracy", "Speed"] }
        ]
      },
      institutional: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Cross-Organization Automation", description: "Standardize processes across institutions", outcomes: ["Consistency", "Compliance"] },
          { title: "Compliance Automation", description: "Automate compliance monitoring and reporting", outcomes: ["Risk reduction", "Audit readiness"] }
        ]
      }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 94, integrationReadiness: 80, lastEvaluated: "2026-03-01" },
    zodiacStandard: true,
    recommendedFor: ["organization", "institutional"],
    website: "https://uipath.com",
    pricing: "Paid",
    opportunityAlignment: {
      businessOpportunities: ["RPA Consulting", "Enterprise Automation"],
      servicePathways: ["RPA Developer", "Automation Architect"],
      careerPathways: ["RPA Engineer", "Process Automation Lead"]
    }
  },

  // ============================================
  // COMMUNICATION & COLLABORATION
  // ============================================
  {
    id: "otter-ai",
    name: "Otter.ai",
    provider: "Otter",
    description: "AI meeting assistant for transcription, summaries, and action items.",
    category: "communication",
    functions: ["Transcription", "Summarization"],
    industries: ["All Industries"],
    scores: {
      reliability: 87,
      workforceFit: 86,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Interview Recording", description: "Record and transcribe interviews for review", outcomes: ["Preparation", "Self-improvement"] },
          { title: "Learning Session Notes", description: "Capture notes from training and learning sessions", outcomes: ["Retention", "Reference"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 90,
        useCases: [
          { title: "Meeting Documentation", description: "Automatically document all meetings", outcomes: ["Accountability", "Knowledge capture"] },
          { title: "Decision Tracking", description: "Track decisions made in meetings", outcomes: ["Follow-through", "Clarity"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 87, integrationReadiness: 90, lastEvaluated: "2026-02-28" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://otter.ai",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Meeting Facilitation", "Documentation Services"],
      servicePathways: ["Meeting Facilitator", "Documentation Specialist"],
      careerPathways: ["Executive Assistant", "Project Coordinator"]
    }
  },
  {
    id: "fireflies-ai",
    name: "Fireflies.ai",
    provider: "Fireflies",
    description: "AI notetaker that records, transcribes, and analyzes voice conversations.",
    category: "communication",
    functions: ["Transcription", "Summarization", "Text Analysis"],
    industries: ["All Industries"],
    scores: {
      reliability: 85,
      workforceFit: 82,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 80,
        useCases: [
          { title: "Interview Preparation Review", description: "Review past interviews to improve", outcomes: ["Self-awareness", "Improvement"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 88,
        useCases: [
          { title: "Sales Call Analysis", description: "Analyze sales conversations for insights", outcomes: ["Performance improvement", "Best practices"] },
          { title: "Meeting Intelligence", description: "Extract insights from meeting patterns", outcomes: ["Process optimization", "Team alignment"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 85, integrationReadiness: 88, lastEvaluated: "2026-02-20" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://fireflies.ai",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Sales Coaching", "Communication Training"],
      servicePathways: ["Sales Coach", "Communication Consultant"],
      careerPathways: ["Sales Manager", "Customer Success Manager"]
    }
  },

  // ============================================
  // RESEARCH & LEARNING
  // ============================================
  {
    id: "perplexity-ai",
    name: "Perplexity AI",
    provider: "Perplexity",
    description: "AI-powered answer engine that provides sourced, up-to-date research responses.",
    category: "research-learning",
    functions: ["Research", "Text Generation", "Summarization"],
    industries: ["All Industries"],
    scores: {
      reliability: 88,
      workforceFit: 90,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 92,
        useCases: [
          { title: "Industry Research", description: "Research industry trends and developments", outcomes: ["Market awareness", "Strategic thinking"] },
          { title: "Skill Learning", description: "Research skills and learning resources", outcomes: ["Learning efficiency", "Resource discovery"] },
          { title: "Career Exploration", description: "Research career paths and requirements", outcomes: ["Informed decisions", "Goal clarity"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Competitive Analysis", description: "Research competitors and market position", outcomes: ["Strategic insights", "Market intelligence"] },
          { title: "Technology Scouting", description: "Discover emerging technologies and trends", outcomes: ["Innovation", "Future-readiness"] }
        ]
      },
      institutional: {
        relevant: true,
        fitScore: 88,
        useCases: [
          { title: "Policy Research", description: "Research policy implications and precedents", outcomes: ["Evidence-based policy", "Comprehensive analysis"] },
          { title: "Trend Analysis", description: "Identify and analyze sector trends", outcomes: ["Strategic planning", "Proactive positioning"] }
        ]
      }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 88, integrationReadiness: 92, lastEvaluated: "2026-03-01" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization", "institutional"],
    website: "https://perplexity.ai",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Research Services", "Market Intelligence"],
      servicePathways: ["Research Analyst", "Market Intelligence Consultant"],
      careerPathways: ["Research Manager", "Strategy Analyst"]
    }
  },
  {
    id: "consensus-ai",
    name: "Consensus",
    provider: "Consensus",
    description: "AI search engine that finds and synthesizes insights from scientific research papers.",
    category: "research-learning",
    functions: ["Research", "Summarization"],
    industries: ["All Industries"],
    scores: {
      reliability: 86,
      workforceFit: 70,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.INTERMEDIATE
    },
    labRelevance: {
      workforce: { relevant: false, fitScore: 0, useCases: [] },
      organization: {
        relevant: true,
        fitScore: 78,
        useCases: [
          { title: "Evidence-Based Decision Making", description: "Find research to support decisions", outcomes: ["Credibility", "Rigor"] }
        ]
      },
      institutional: {
        relevant: true,
        fitScore: 92,
        useCases: [
          { title: "Academic Research", description: "Search academic literature efficiently", outcomes: ["Research speed", "Comprehensive coverage"] },
          { title: "Policy Evidence", description: "Find evidence to support policy positions", outcomes: ["Evidence-based policy", "Credibility"] }
        ]
      }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 86, integrationReadiness: 88, lastEvaluated: "2026-02-15" },
    zodiacStandard: true,
    recommendedFor: ["organization", "institutional"],
    website: "https://consensus.app",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Research Consulting", "Evidence Synthesis"],
      servicePathways: ["Research Consultant", "Evidence Analyst"],
      careerPathways: ["Research Director", "Policy Analyst"]
    }
  },
  {
    id: "elicit-ai",
    name: "Elicit",
    provider: "Elicit",
    description: "AI research assistant that automates literature review and research workflows.",
    category: "research-learning",
    functions: ["Research", "Summarization", "Text Analysis"],
    industries: ["Education", "Healthcare", "Government"],
    scores: {
      reliability: 84,
      workforceFit: 65,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.INTERMEDIATE
    },
    labRelevance: {
      workforce: { relevant: false, fitScore: 0, useCases: [] },
      organization: { relevant: false, fitScore: 0, useCases: [] },
      institutional: {
        relevant: true,
        fitScore: 90,
        useCases: [
          { title: "Systematic Reviews", description: "Conduct systematic literature reviews", outcomes: ["Rigor", "Comprehensiveness"] },
          { title: "Research Synthesis", description: "Synthesize findings across studies", outcomes: ["Meta-analysis", "Pattern identification"] },
          { title: "Evidence Mapping", description: "Map evidence landscapes", outcomes: ["Research gaps", "Priority identification"] }
        ]
      }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 84, integrationReadiness: 85, lastEvaluated: "2026-02-28" },
    zodiacStandard: true,
    recommendedFor: ["institutional"],
    website: "https://elicit.org",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Research Services", "Literature Review Services"],
      servicePathways: ["Systematic Review Specialist", "Research Methodologist"],
      careerPathways: ["Research Scientist", "Academic Researcher"]
    }
  },

  // ============================================
  // CREATIVE & DESIGN
  // ============================================
  {
    id: "midjourney",
    name: "Midjourney",
    provider: "Midjourney",
    description: "AI image generation tool creating high-quality artistic and photorealistic images.",
    category: "creative-design",
    functions: ["Image Generation"],
    industries: ["All Industries"],
    scores: {
      reliability: 90,
      workforceFit: 75,
      integrationComplexity: INTEGRATION_COMPLEXITY.MEDIUM,
      aiReadinessLevel: AI_READINESS_LEVEL.INTERMEDIATE
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 78,
        useCases: [
          { title: "Portfolio Visuals", description: "Create unique visuals for portfolios", outcomes: ["Stand out", "Creative expression"] },
          { title: "Personal Branding", description: "Generate branded imagery", outcomes: ["Visual identity", "Professional presence"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 82,
        useCases: [
          { title: "Marketing Visuals", description: "Generate marketing and campaign imagery", outcomes: ["Content velocity", "Visual consistency"] },
          { title: "Concept Visualization", description: "Visualize concepts and ideas", outcomes: ["Communication", "Stakeholder alignment"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 90, integrationReadiness: 75, lastEvaluated: "2026-02-20" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://midjourney.com",
    pricing: "Paid",
    opportunityAlignment: {
      businessOpportunities: ["Creative Agency", "Visual Content Services"],
      servicePathways: ["AI Artist", "Visual Designer"],
      careerPathways: ["Creative Director", "Brand Designer"]
    }
  },
  {
    id: "canva-ai",
    name: "Canva AI (Magic Studio)",
    provider: "Canva",
    description: "AI-powered design platform with text-to-image, background removal, and smart editing.",
    category: "creative-design",
    functions: ["Image Generation", "Text Generation", "Image Analysis"],
    industries: ["All Industries"],
    scores: {
      reliability: 92,
      workforceFit: 88,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 90,
        useCases: [
          { title: "Resume Design", description: "Create visually appealing resumes", outcomes: ["Professional presentation", "Stand out"] },
          { title: "Portfolio Creation", description: "Design portfolio presentations", outcomes: ["Visual storytelling", "Professional image"] },
          { title: "Personal Presentations", description: "Create impactful presentations", outcomes: ["Communication", "Influence"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 88,
        useCases: [
          { title: "Marketing Materials", description: "Design marketing collateral", outcomes: ["Brand consistency", "Speed"] },
          { title: "Training Decks", description: "Create engaging training materials", outcomes: ["Learning engagement", "Professional quality"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 92, integrationReadiness: 95, lastEvaluated: "2026-03-01" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://canva.com",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Design Services", "Brand Consulting"],
      servicePathways: ["Graphic Designer", "Brand Consultant"],
      careerPathways: ["Marketing Designer", "Visual Communications Specialist"]
    }
  },

  // ============================================
  // CODING & DEVELOPMENT
  // ============================================
  {
    id: "github-copilot",
    name: "GitHub Copilot",
    provider: "GitHub/Microsoft",
    description: "AI pair programmer that suggests code completions and entire functions.",
    category: "coding-development",
    functions: ["Coding Assistance", "Text Generation"],
    industries: ["Technology"],
    scores: {
      reliability: 91,
      workforceFit: 82,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.INTERMEDIATE
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Coding Skill Development", description: "Accelerate learning to code", outcomes: ["Faster learning", "Best practices"] },
          { title: "Portfolio Projects", description: "Build portfolio projects faster", outcomes: ["More projects", "Higher quality"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 90,
        useCases: [
          { title: "Developer Productivity", description: "Increase developer output", outcomes: ["Speed", "Quality"] },
          { title: "Code Quality", description: "Improve code quality and consistency", outcomes: ["Maintainability", "Standards"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 91, integrationReadiness: 93, lastEvaluated: "2026-03-01" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://github.com/features/copilot",
    pricing: "Paid",
    opportunityAlignment: {
      businessOpportunities: ["Software Development", "Tech Consulting"],
      servicePathways: ["Software Developer", "Technical Consultant"],
      careerPathways: ["Software Engineer", "Technical Lead"]
    }
  },
  {
    id: "cursor-ai",
    name: "Cursor",
    provider: "Cursor",
    description: "AI-first code editor built for pair programming with AI assistance.",
    category: "coding-development",
    functions: ["Coding Assistance", "Text Generation"],
    industries: ["Technology"],
    scores: {
      reliability: 82,
      workforceFit: 80,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.INTERMEDIATE
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 82,
        useCases: [
          { title: "Learning to Code", description: "Learn programming with AI guidance", outcomes: ["Accelerated learning", "Confidence"] },
          { title: "Technical Upskilling", description: "Upgrade technical skills with AI support", outcomes: ["Skill expansion", "Market relevance"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 78,
        useCases: [
          { title: "Rapid Prototyping", description: "Build prototypes quickly with AI", outcomes: ["Speed to market", "Experimentation"] },
          { title: "Code Review Assistance", description: "AI-assisted code review", outcomes: ["Quality", "Knowledge sharing"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.UNDER_REVIEW, reliabilityScore: 82, integrationReadiness: 85, lastEvaluated: "2026-02-28" },
    zodiacStandard: false,
    recommendedFor: ["workforce", "organization"],
    website: "https://cursor.sh",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["AI Development", "Tech Education"],
      servicePathways: ["AI Developer", "Tech Trainer"],
      careerPathways: ["Full-Stack Developer", "AI Engineer"]
    }
  },

  // ============================================
  // PROJECT MANAGEMENT
  // ============================================
  {
    id: "notion-ai",
    name: "Notion AI",
    provider: "Notion",
    description: "AI assistant integrated into the Notion workspace for writing, summarizing, and organizing.",
    category: "project-management",
    functions: ["Text Generation", "Summarization", "Project Planning"],
    industries: ["All Industries"],
    scores: {
      reliability: 89,
      workforceFit: 88,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 90,
        useCases: [
          { title: "Personal Knowledge Management", description: "Organize and connect personal knowledge", outcomes: ["Knowledge retention", "Idea connection"] },
          { title: "Goal Tracking", description: "Set and track personal goals", outcomes: ["Accountability", "Progress visibility"] },
          { title: "Learning Notes", description: "Organize and enhance learning notes", outcomes: ["Retention", "Application"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 92,
        useCases: [
          { title: "Team Documentation", description: "Create and maintain team knowledge bases", outcomes: ["Knowledge sharing", "Onboarding"] },
          { title: "Project Management", description: "Manage projects with AI assistance", outcomes: ["Clarity", "Efficiency"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 89, integrationReadiness: 92, lastEvaluated: "2026-02-25" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://notion.so",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Productivity Consulting", "Knowledge Management"],
      servicePathways: ["Productivity Consultant", "Knowledge Manager"],
      careerPathways: ["Operations Manager", "Knowledge Management Specialist"]
    }
  },
  {
    id: "clickup-ai",
    name: "ClickUp AI",
    provider: "ClickUp",
    description: "AI-powered project management with task automation, content generation, and summaries.",
    category: "project-management",
    functions: ["Text Generation", "Summarization", "Project Planning", "Automation"],
    industries: ["All Industries"],
    scores: {
      reliability: 87,
      workforceFit: 85,
      integrationComplexity: INTEGRATION_COMPLEXITY.LOW,
      aiReadinessLevel: AI_READINESS_LEVEL.BASIC
    },
    labRelevance: {
      workforce: {
        relevant: true,
        fitScore: 85,
        useCases: [
          { title: "Personal Task Management", description: "Manage personal tasks and projects", outcomes: ["Organization", "Productivity"] },
          { title: "Goal Breakdown", description: "Break down goals into actionable tasks", outcomes: ["Clarity", "Progress"] }
        ]
      },
      organization: {
        relevant: true,
        fitScore: 90,
        useCases: [
          { title: "Team Project Management", description: "Manage team projects with AI", outcomes: ["Coordination", "Visibility"] },
          { title: "Progress Tracking", description: "Track progress across projects", outcomes: ["Accountability", "Insights"] }
        ]
      },
      institutional: { relevant: false, fitScore: 0, useCases: [] }
    },
    evaluation: { status: EVALUATION_STATUS.VERIFIED, reliabilityScore: 87, integrationReadiness: 90, lastEvaluated: "2026-03-01" },
    zodiacStandard: true,
    recommendedFor: ["workforce", "organization"],
    website: "https://clickup.com",
    pricing: "Freemium",
    opportunityAlignment: {
      businessOpportunities: ["Project Management Consulting", "Process Optimization"],
      servicePathways: ["Project Manager", "Process Consultant"],
      careerPathways: ["Project Manager", "Program Manager"]
    }
  }
];

// ============================================
// TOOL STACKS - Curated Combinations
// ============================================

export const TOOL_STACKS = [
  // WORKFORCE STACKS
  {
    id: "workforce-starter",
    name: "Workforce Starter Stack",
    targetLab: "workforce",
    purpose: "Essential AI tools for individual career development and job seeking",
    targetUseCase: "Job seekers and career changers looking to leverage AI for career advancement",
    aiReadinessRequired: AI_READINESS_LEVEL.BASIC,
    tools: ["chatgpt-4", "grammarly", "canva-ai", "notion-ai"],
    description: "A foundational stack for anyone starting their AI-augmented career journey. Covers writing, communication, visual presentation, and personal organization.",
    outcomes: [
      "Professional resume and cover letters",
      "Polished written communication",
      "Visual portfolio and presentations",
      "Organized career development plan"
    ],
    estimatedSetupTime: "1-2 hours",
    monthlyInvestment: "$20-40"
  },
  {
    id: "workforce-research",
    name: "Career Research Stack",
    targetLab: "workforce",
    purpose: "Tools for researching industries, roles, and skill requirements",
    targetUseCase: "Professionals exploring career transitions or advancement paths",
    aiReadinessRequired: AI_READINESS_LEVEL.BASIC,
    tools: ["perplexity-ai", "chatgpt-4", "otter-ai"],
    description: "Research and learn about new industries, roles, and skills. Perfect for career exploration and planning.",
    outcomes: [
      "Deep industry understanding",
      "Clear skill gap identification",
      "Documented learning from interviews and conversations"
    ],
    estimatedSetupTime: "30 minutes",
    monthlyInvestment: "$0-30"
  },
  {
    id: "workforce-builder",
    name: "Skill Builder Stack",
    targetLab: "workforce",
    purpose: "Accelerate learning and skill development with AI assistance",
    targetUseCase: "Professionals upskilling or learning new technical/professional skills",
    aiReadinessRequired: AI_READINESS_LEVEL.INTERMEDIATE,
    tools: ["claude-3", "notion-ai", "github-copilot", "perplexity-ai"],
    description: "Comprehensive stack for serious skill development, combining learning assistance, knowledge management, and practical application.",
    outcomes: [
      "Accelerated learning curve",
      "Organized knowledge base",
      "Practical skill application",
      "Continuous improvement tracking"
    ],
    estimatedSetupTime: "2-3 hours",
    monthlyInvestment: "$30-60"
  },

  // ORGANIZATION STACKS
  {
    id: "org-efficiency",
    name: "Organization Efficiency Stack",
    targetLab: "organization",
    purpose: "Core tools for organizational productivity and workflow optimization",
    targetUseCase: "Teams looking to improve operational efficiency through AI",
    aiReadinessRequired: AI_READINESS_LEVEL.BASIC,
    tools: ["chatgpt-4", "zapier-ai", "otter-ai", "clickup-ai"],
    description: "Essential stack for organizations starting their AI integration journey. Covers communication, automation, meeting management, and project tracking.",
    outcomes: [
      "Streamlined communication",
      "Automated repetitive tasks",
      "Complete meeting documentation",
      "Improved project visibility"
    ],
    estimatedSetupTime: "4-6 hours",
    monthlyInvestment: "$100-200"
  },
  {
    id: "org-analytics",
    name: "Data-Driven Organization Stack",
    targetLab: "organization",
    purpose: "Analytics and data visualization for organizational decision-making",
    targetUseCase: "Organizations wanting to make data-driven workforce decisions",
    aiReadinessRequired: AI_READINESS_LEVEL.INTERMEDIATE,
    tools: ["power-bi-copilot", "chatgpt-4", "julius-ai", "notion-ai"],
    description: "Analytics-focused stack for organizations ready to leverage data for workforce insights and strategic planning.",
    outcomes: [
      "Executive dashboards",
      "Workforce analytics",
      "Ad-hoc data exploration",
      "Documented insights and decisions"
    ],
    estimatedSetupTime: "8-12 hours",
    monthlyInvestment: "$200-400"
  },
  {
    id: "org-enterprise",
    name: "Enterprise Automation Stack",
    targetLab: "organization",
    purpose: "Enterprise-grade process automation and integration",
    targetUseCase: "Large organizations with complex processes and systems",
    aiReadinessRequired: AI_READINESS_LEVEL.ADVANCED,
    tools: ["uipath", "power-bi-copilot", "make-ai", "fireflies-ai"],
    description: "Advanced stack for enterprise-level automation, analytics, and process optimization.",
    outcomes: [
      "End-to-end process automation",
      "Enterprise-wide visibility",
      "Cross-system integration",
      "Conversation intelligence"
    ],
    estimatedSetupTime: "40+ hours",
    monthlyInvestment: "$500+"
  },

  // INSTITUTIONAL STACKS
  {
    id: "inst-research",
    name: "Research Intelligence Stack",
    targetLab: "institutional",
    purpose: "Tools for systematic research, evidence synthesis, and policy support",
    targetUseCase: "Research institutions, policy organizations, and academic bodies",
    aiReadinessRequired: AI_READINESS_LEVEL.INTERMEDIATE,
    tools: ["elicit-ai", "consensus-ai", "perplexity-ai", "claude-3"],
    description: "Comprehensive research stack for institutions conducting systematic reviews, policy research, and evidence synthesis.",
    outcomes: [
      "Systematic literature reviews",
      "Evidence-based policy support",
      "Comprehensive research synthesis",
      "High-quality analytical outputs"
    ],
    estimatedSetupTime: "4-8 hours",
    monthlyInvestment: "$50-150"
  },
  {
    id: "inst-analytics",
    name: "Sector Intelligence Stack",
    targetLab: "institutional",
    purpose: "Analytics and benchmarking across institutions and sectors",
    targetUseCase: "Government agencies, industry associations, and sector bodies",
    aiReadinessRequired: AI_READINESS_LEVEL.INTERMEDIATE,
    tools: ["tableau-ai", "power-bi-copilot", "perplexity-ai", "chatgpt-4"],
    description: "Analytics stack for sector-level intelligence, benchmarking, and cross-institutional comparison.",
    outcomes: [
      "Sector benchmarking dashboards",
      "Cross-institutional analytics",
      "Trend identification",
      "Policy impact visualization"
    ],
    estimatedSetupTime: "12-20 hours",
    monthlyInvestment: "$300-500"
  }
];

// ============================================
// OPPORTUNITY INTELLIGENCE ENGINE DATA
// ============================================

export const OPPORTUNITY_CATEGORIES = {
  BUSINESS: "business",
  SERVICE: "service",
  CAREER: "career"
};

// Difficulty Levels
export const DIFFICULTY_LEVEL = {
  BEGINNER: "beginner",
  INTERMEDIATE: "intermediate",
  ADVANCED: "advanced"
};

// Demand Levels
export const DEMAND_LEVEL = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  VERY_HIGH: "very high"
};

// Skill-to-Category Mapping for intelligent tool matching
export const SKILL_TOOL_MAPPING = {
  "content creation": { categories: ["content-writing", "creative-design"], functions: ["Text Generation", "Image Generation"] },
  "content strategy": { categories: ["content-writing"], functions: ["Text Generation", "Text Analysis"] },
  "AI writing tools": { categories: ["content-writing"], functions: ["Text Generation"] },
  "automation": { categories: ["automation-workflow"], functions: ["Automation"] },
  "workflow design": { categories: ["automation-workflow", "project-management"], functions: ["Automation", "Project Planning"] },
  "data analysis": { categories: ["data-analytics"], functions: ["Data Analysis"] },
  "analytics": { categories: ["data-analytics"], functions: ["Data Analysis"] },
  "research": { categories: ["research-learning"], functions: ["Research", "Summarization"] },
  "research methodology": { categories: ["research-learning"], functions: ["Research"] },
  "AI research tools": { categories: ["research-learning"], functions: ["Research", "Summarization"] },
  "coding": { categories: ["coding-development"], functions: ["Coding Assistance"] },
  "design": { categories: ["creative-design"], functions: ["Image Generation", "Presentation"] },
  "teaching": { categories: ["content-writing", "research-learning"], functions: ["Text Generation", "Summarization"] },
  "communication": { categories: ["communication"], functions: ["Text Generation", "Translation"] },
  "project management": { categories: ["project-management"], functions: ["Project Planning", "Automation"] },
  "business analysis": { categories: ["data-analytics", "project-management"], functions: ["Data Analysis", "Summarization"] },
  "change management": { categories: ["communication", "project-management"], functions: ["Text Generation", "Project Planning"] },
  "client management": { categories: ["communication", "project-management"], functions: ["Text Generation", "Project Planning"] },
  "career development": { categories: ["content-writing", "research-learning"], functions: ["Text Generation", "Research"] },
  "curriculum design": { categories: ["content-writing", "research-learning"], functions: ["Text Generation", "Summarization"] },
  "instructional design": { categories: ["content-writing", "creative-design"], functions: ["Text Generation", "Presentation"] },
  "workforce planning": { categories: ["data-analytics"], functions: ["Data Analysis"] },
  "AI tool proficiency": { categories: ["content-writing", "automation-workflow"], functions: ["Text Generation", "Automation"] },
  "AI tool expertise": { categories: ["content-writing", "automation-workflow", "data-analytics"], functions: ["Text Generation", "Automation", "Data Analysis"] },
  "AI tools": { categories: ["content-writing", "automation-workflow"], functions: ["Text Generation", "Automation"] },
  "operations": { categories: ["automation-workflow", "project-management"], functions: ["Automation", "Project Planning"] },
  "learning technology": { categories: ["content-writing", "creative-design"], functions: ["Text Generation", "Presentation"] },
  "AI analytics tools": { categories: ["data-analytics"], functions: ["Data Analysis"] },
  "coaching certification": { categories: ["content-writing"], functions: ["Text Generation"] },
  "analysis": { categories: ["data-analytics", "research-learning"], functions: ["Data Analysis", "Research"] },
  "automation tools": { categories: ["automation-workflow"], functions: ["Automation"] }
};

// WAIT Score Thresholds for future personalization
export const WAIT_SCORE_THRESHOLDS = {
  trust: { low: 40, medium: 60, high: 80 },
  alignment: { low: 40, medium: 60, high: 80 },
  readiness: { low: 40, medium: 60, high: 80 },
  aiCapability: { low: 40, medium: 60, high: 80 },
  resilience: { low: 40, medium: 60, high: 80 }
};

// User Profile Template (for future personalization)
export const USER_PROFILE_TEMPLATE = {
  waitScores: {
    trust: null,
    alignment: null,
    readiness: null,
    aiCapability: null,
    resilience: null
  },
  currentSkills: [],
  targetOpportunities: [],
  experienceLevel: null, // beginner, intermediate, advanced
  industryFocus: null,
  preferredTools: []
};

export const OPPORTUNITY_DATABASE = {
  // Business Opportunities (Enhanced)
  business: [
    {
      id: "ai-consulting",
      name: "AI Implementation Consulting",
      category: OPPORTUNITY_CATEGORIES.BUSINESS,
      description: "Help organizations integrate AI tools into their workflows and processes. Guide teams through tool selection, implementation, and change management.",
      requiredSkills: ["AI tool proficiency", "Business analysis", "Change management"],
      recommendedTools: ["chatgpt-4", "zapier-ai", "notion-ai"],
      potentialRevenue: "$5,000-25,000/project",
      marketDemand: DEMAND_LEVEL.HIGH,
      entryBarrier: "medium",
      difficultyLevel: DIFFICULTY_LEVEL.INTERMEDIATE,
      pathwaySteps: [
        { step: 1, title: "Build AI Expertise", description: "Master 5-10 AI tools across different categories" },
        { step: 2, title: "Develop Methodology", description: "Create a structured approach for AI assessment and implementation" },
        { step: 3, title: "Case Study Portfolio", description: "Document 3-5 successful implementations (can start with your own projects)" },
        { step: 4, title: "Network Building", description: "Connect with potential clients through LinkedIn, local business groups" },
        { step: 5, title: "Service Packaging", description: "Create clear service offerings with defined deliverables and pricing" }
      ],
      waitScoreRequirements: {
        minAiCapability: 70,
        minAlignment: 60,
        minTrust: 50
      },
      industryFit: ["Technology", "Finance", "Healthcare", "Education"]
    },
    {
      id: "content-agency",
      name: "AI-Powered Content Agency",
      category: OPPORTUNITY_CATEGORIES.BUSINESS,
      description: "Provide content creation services using AI tools for efficiency and scale. Deliver blogs, social media, marketing copy, and documentation at competitive rates.",
      requiredSkills: ["Content strategy", "AI writing tools", "Client management"],
      recommendedTools: ["chatgpt-4", "jasper-ai", "canva-ai"],
      potentialRevenue: "$3,000-15,000/month",
      marketDemand: DEMAND_LEVEL.HIGH,
      entryBarrier: "low",
      difficultyLevel: DIFFICULTY_LEVEL.BEGINNER,
      pathwaySteps: [
        { step: 1, title: "Master AI Writing", description: "Become proficient with ChatGPT, Jasper, and editing tools" },
        { step: 2, title: "Build Portfolio", description: "Create 10-15 sample pieces across different content types" },
        { step: 3, title: "Define Services", description: "Package offerings: blog posts, social media, newsletters, etc." },
        { step: 4, title: "Platform Presence", description: "Set up profiles on Upwork, Fiverr, or launch your own website" },
        { step: 5, title: "Scale with Systems", description: "Create templates, workflows, and quality processes" }
      ],
      waitScoreRequirements: {
        minAiCapability: 50,
        minAlignment: 40,
        minTrust: 40
      },
      industryFit: ["All Industries"]
    },
    {
      id: "automation-services",
      name: "Business Process Automation",
      category: OPPORTUNITY_CATEGORIES.BUSINESS,
      description: "Design and implement automation solutions for small and medium businesses. Connect apps, automate workflows, and reduce manual work.",
      requiredSkills: ["Workflow design", "Automation tools", "Business analysis"],
      recommendedTools: ["zapier-ai", "make-ai", "clickup-ai"],
      potentialRevenue: "$2,000-10,000/project",
      marketDemand: DEMAND_LEVEL.HIGH,
      entryBarrier: "medium",
      difficultyLevel: DIFFICULTY_LEVEL.INTERMEDIATE,
      pathwaySteps: [
        { step: 1, title: "Learn Automation Platforms", description: "Master Zapier, Make (Integromat), and workflow logic" },
        { step: 2, title: "Process Mapping Skills", description: "Learn to document and optimize business processes" },
        { step: 3, title: "Build Demo Automations", description: "Create 5-10 example automations for common business needs" },
        { step: 4, title: "Target Market Selection", description: "Focus on specific industries or business types" },
        { step: 5, title: "Productize Services", description: "Create packaged automation solutions with clear ROI" }
      ],
      waitScoreRequirements: {
        minAiCapability: 65,
        minAlignment: 55,
        minTrust: 50
      },
      industryFit: ["Technology", "Retail", "Finance", "Logistics"]
    },
    {
      id: "ai-training-business",
      name: "AI Training & Upskilling Company",
      category: OPPORTUNITY_CATEGORIES.BUSINESS,
      description: "Build a training company that helps organizations upskill their workforce on AI tools. Offer workshops, courses, and certification programs.",
      requiredSkills: ["AI tool expertise", "Teaching ability", "Business development"],
      recommendedTools: ["chatgpt-4", "notion-ai", "canva-ai", "gamma-ai"],
      potentialRevenue: "$10,000-50,000/month",
      marketDemand: DEMAND_LEVEL.VERY_HIGH,
      entryBarrier: "medium",
      difficultyLevel: DIFFICULTY_LEVEL.INTERMEDIATE,
      pathwaySteps: [
        { step: 1, title: "Deep AI Mastery", description: "Become expert-level in 15+ AI tools across categories" },
        { step: 2, title: "Curriculum Development", description: "Create structured training programs for different skill levels" },
        { step: 3, title: "Pilot Programs", description: "Run free or low-cost workshops to refine approach" },
        { step: 4, title: "Corporate Outreach", description: "Target HR departments and L&D teams" },
        { step: 5, title: "Scale Delivery", description: "Train other trainers, create online courses, expand reach" }
      ],
      waitScoreRequirements: {
        minAiCapability: 80,
        minAlignment: 70,
        minTrust: 65
      },
      industryFit: ["Education", "Technology", "Finance", "Healthcare"]
    }
  ],
  
  // Service Pathways (Enhanced)
  service: [
    {
      id: "ai-trainer",
      name: "AI Tools Trainer",
      category: OPPORTUNITY_CATEGORIES.SERVICE,
      description: "Train individuals and teams on effective AI tool usage. Deliver workshops, one-on-one coaching, and create learning materials.",
      requiredSkills: ["AI tool expertise", "Teaching ability", "Curriculum design"],
      recommendedTools: ["chatgpt-4", "claude-3", "notion-ai"],
      hourlyRate: "$75-200/hour",
      marketDemand: DEMAND_LEVEL.VERY_HIGH,
      entryBarrier: "low",
      difficultyLevel: DIFFICULTY_LEVEL.BEGINNER,
      pathwaySteps: [
        { step: 1, title: "Tool Proficiency", description: "Master the most in-demand AI tools thoroughly" },
        { step: 2, title: "Teaching Framework", description: "Develop a methodology for teaching AI effectively" },
        { step: 3, title: "Content Creation", description: "Build training materials, exercises, and assessments" },
        { step: 4, title: "Platform Building", description: "Create online presence and start offering sessions" },
        { step: 5, title: "Specialize", description: "Focus on specific industries or tool categories" }
      ],
      waitScoreRequirements: {
        minAiCapability: 60,
        minAlignment: 50,
        minTrust: 55
      },
      industryFit: ["All Industries"]
    },
    {
      id: "ai-career-coach",
      name: "AI-Augmented Career Coach",
      category: OPPORTUNITY_CATEGORIES.SERVICE,
      description: "Provide career coaching services enhanced by AI analysis and tools. Help clients with resume optimization, interview prep, and career strategy.",
      requiredSkills: ["Career development", "AI tools", "Coaching certification"],
      recommendedTools: ["chatgpt-4", "perplexity-ai", "grammarly"],
      hourlyRate: "$100-300/hour",
      marketDemand: DEMAND_LEVEL.HIGH,
      entryBarrier: "medium",
      difficultyLevel: DIFFICULTY_LEVEL.INTERMEDIATE,
      pathwaySteps: [
        { step: 1, title: "Coaching Foundation", description: "Get certified in career coaching methodologies" },
        { step: 2, title: "AI Integration", description: "Learn to use AI tools for resume analysis, market research" },
        { step: 3, title: "Service Design", description: "Create coaching packages combining human insight + AI efficiency" },
        { step: 4, title: "Client Acquisition", description: "Build reputation through testimonials and referrals" },
        { step: 5, title: "Premium Positioning", description: "Develop signature methodology and premium offerings" }
      ],
      waitScoreRequirements: {
        minAiCapability: 55,
        minAlignment: 60,
        minTrust: 70
      },
      industryFit: ["All Industries"]
    },
    {
      id: "research-analyst",
      name: "AI Research Analyst",
      category: OPPORTUNITY_CATEGORIES.SERVICE,
      description: "Provide research services using AI tools for faster, deeper analysis. Deliver market research, competitive analysis, and trend reports.",
      requiredSkills: ["Research methodology", "AI research tools", "Analysis"],
      recommendedTools: ["perplexity-ai", "elicit-ai", "consensus-ai"],
      hourlyRate: "$50-150/hour",
      marketDemand: DEMAND_LEVEL.MEDIUM,
      entryBarrier: "medium",
      difficultyLevel: DIFFICULTY_LEVEL.INTERMEDIATE,
      pathwaySteps: [
        { step: 1, title: "Research Skills", description: "Develop strong research methodology and critical analysis" },
        { step: 2, title: "AI Research Tools", description: "Master Perplexity, Elicit, Consensus and academic databases" },
        { step: 3, title: "Deliverable Templates", description: "Create professional report templates and formats" },
        { step: 4, title: "Niche Selection", description: "Focus on specific research domains (market, academic, policy)" },
        { step: 5, title: "Client Base", description: "Build relationships with consulting firms, agencies, academics" }
      ],
      waitScoreRequirements: {
        minAiCapability: 50,
        minAlignment: 55,
        minReadiness: 60
      },
      industryFit: ["Technology", "Healthcare", "Finance", "Government"]
    },
    {
      id: "prompt-engineer",
      name: "Prompt Engineering Specialist",
      category: OPPORTUNITY_CATEGORIES.SERVICE,
      description: "Help businesses optimize their AI interactions through expert prompt engineering. Create prompt libraries and AI interaction strategies.",
      requiredSkills: ["AI tool expertise", "Communication", "Technical writing"],
      recommendedTools: ["chatgpt-4", "claude-3", "midjourney"],
      hourlyRate: "$80-250/hour",
      marketDemand: DEMAND_LEVEL.HIGH,
      entryBarrier: "low",
      difficultyLevel: DIFFICULTY_LEVEL.BEGINNER,
      pathwaySteps: [
        { step: 1, title: "Prompt Mastery", description: "Learn advanced prompting techniques across LLMs" },
        { step: 2, title: "Use Case Library", description: "Build a library of optimized prompts for common tasks" },
        { step: 3, title: "Testing Framework", description: "Develop methods to measure and improve prompt effectiveness" },
        { step: 4, title: "Documentation", description: "Create clear guides and training on prompt usage" },
        { step: 5, title: "Enterprise Consulting", description: "Help organizations build internal prompt libraries" }
      ],
      waitScoreRequirements: {
        minAiCapability: 70,
        minAlignment: 45,
        minTrust: 50
      },
      industryFit: ["Technology", "Marketing", "Education", "All Industries"]
    }
  ],
  
  // Career Pathways (Enhanced)
  career: [
    {
      id: "ai-ops-specialist",
      name: "AI Operations Specialist",
      category: OPPORTUNITY_CATEGORIES.CAREER,
      description: "Manage AI tool deployment and optimization within organizations. Oversee AI governance, vendor relationships, and user adoption.",
      requiredSkills: ["AI tools", "Operations", "Project management"],
      recommendedTools: ["chatgpt-4", "zapier-ai", "power-bi-copilot"],
      salaryRange: "$70,000-120,000/year",
      growthOutlook: DEMAND_LEVEL.VERY_HIGH,
      pathwayFrom: ["Operations Analyst", "IT Support", "Project Manager"],
      difficultyLevel: DIFFICULTY_LEVEL.INTERMEDIATE,
      pathwaySteps: [
        { step: 1, title: "Build Foundation", description: "Gain experience in operations, IT, or project management" },
        { step: 2, title: "AI Tool Expertise", description: "Develop proficiency across major AI tools and platforms" },
        { step: 3, title: "Governance Knowledge", description: "Learn AI ethics, security, and compliance requirements" },
        { step: 4, title: "Internal Champion", description: "Lead AI initiatives within current organization" },
        { step: 5, title: "Role Transition", description: "Move into dedicated AI Operations position" }
      ],
      waitScoreRequirements: {
        minAiCapability: 65,
        minAlignment: 70,
        minReadiness: 60
      },
      industryFit: ["Technology", "Finance", "Healthcare", "Manufacturing"]
    },
    {
      id: "workforce-analyst",
      name: "Workforce Intelligence Analyst",
      category: OPPORTUNITY_CATEGORIES.CAREER,
      description: "Analyze workforce data and trends to inform organizational strategy. Use AI tools to identify patterns, predict needs, and optimize talent decisions.",
      requiredSkills: ["Data analysis", "Workforce planning", "AI analytics tools"],
      recommendedTools: ["power-bi-copilot", "tableau-ai", "chatgpt-4"],
      salaryRange: "$75,000-130,000/year",
      growthOutlook: DEMAND_LEVEL.HIGH,
      pathwayFrom: ["HR Analyst", "Business Analyst", "Data Analyst"],
      difficultyLevel: DIFFICULTY_LEVEL.INTERMEDIATE,
      pathwaySteps: [
        { step: 1, title: "Analytics Foundation", description: "Build strong data analysis and visualization skills" },
        { step: 2, title: "HR Domain Knowledge", description: "Understand workforce metrics, planning, and strategy" },
        { step: 3, title: "AI-Powered Analytics", description: "Master AI analytics tools for workforce insights" },
        { step: 4, title: "Strategic Positioning", description: "Connect analysis to business outcomes and decisions" },
        { step: 5, title: "Leadership Track", description: "Move toward Workforce Intelligence leadership roles" }
      ],
      waitScoreRequirements: {
        minAiCapability: 60,
        minAlignment: 65,
        minReadiness: 55
      },
      industryFit: ["All Industries"]
    },
    {
      id: "ai-learning-designer",
      name: "AI Learning Experience Designer",
      category: OPPORTUNITY_CATEGORIES.CAREER,
      description: "Design AI-enhanced learning experiences and training programs. Create adaptive, personalized learning journeys using AI tools.",
      requiredSkills: ["Instructional design", "AI tools", "Learning technology"],
      recommendedTools: ["chatgpt-4", "canva-ai", "notion-ai"],
      salaryRange: "$65,000-110,000/year",
      growthOutlook: DEMAND_LEVEL.HIGH,
      pathwayFrom: ["Instructional Designer", "Trainer", "Curriculum Developer"],
      difficultyLevel: DIFFICULTY_LEVEL.INTERMEDIATE,
      pathwaySteps: [
        { step: 1, title: "ID Foundation", description: "Master instructional design principles and methodologies" },
        { step: 2, title: "AI Content Creation", description: "Learn to use AI for content development and assessment" },
        { step: 3, title: "Adaptive Learning", description: "Understand personalization and adaptive learning approaches" },
        { step: 4, title: "Portfolio Building", description: "Create showcase of AI-enhanced learning experiences" },
        { step: 5, title: "Specialization", description: "Focus on specific domains (corporate, higher ed, K-12)" }
      ],
      waitScoreRequirements: {
        minAiCapability: 55,
        minAlignment: 60,
        minReadiness: 50
      },
      industryFit: ["Education", "Technology", "Healthcare", "Finance"]
    },
    {
      id: "ai-product-manager",
      name: "AI Product Manager",
      category: OPPORTUNITY_CATEGORIES.CAREER,
      description: "Lead the development and strategy of AI-powered products and features. Bridge technical AI capabilities with user needs and business value.",
      requiredSkills: ["Product management", "AI tool expertise", "Business strategy"],
      recommendedTools: ["chatgpt-4", "notion-ai", "clickup-ai"],
      salaryRange: "$100,000-180,000/year",
      growthOutlook: DEMAND_LEVEL.VERY_HIGH,
      pathwayFrom: ["Product Manager", "Business Analyst", "Technical Lead"],
      difficultyLevel: DIFFICULTY_LEVEL.ADVANCED,
      pathwaySteps: [
        { step: 1, title: "PM Fundamentals", description: "Build strong product management foundation" },
        { step: 2, title: "AI Literacy", description: "Understand AI capabilities, limitations, and trends" },
        { step: 3, title: "Technical Depth", description: "Develop ability to communicate with AI/ML teams" },
        { step: 4, title: "AI Product Experience", description: "Lead or contribute to AI feature development" },
        { step: 5, title: "Strategic Leadership", description: "Drive AI product strategy and roadmap" }
      ],
      waitScoreRequirements: {
        minAiCapability: 75,
        minAlignment: 75,
        minTrust: 70
      },
      industryFit: ["Technology", "Finance", "Healthcare"]
    }
  ]
};

// ============================================
// HELPER FUNCTIONS
// ============================================

export function getToolsByLab(labType) {
  return AI_REGISTRY.filter(tool => tool.labRelevance[labType]?.relevant === true);
}

export function getToolsByCategory(categoryId) {
  return AI_REGISTRY.filter(tool => tool.category === categoryId);
}

export function getVerifiedTools() {
  return AI_REGISTRY.filter(tool => tool.evaluation.status === EVALUATION_STATUS.VERIFIED);
}

export function getToolsByFunction(functionName) {
  return AI_REGISTRY.filter(tool => tool.functions.includes(functionName));
}

export function getToolById(toolId) {
  return AI_REGISTRY.find(tool => tool.id === toolId);
}

export function getToolStacksByLab(labType) {
  return TOOL_STACKS.filter(stack => stack.targetLab === labType);
}

export function getToolStackById(stackId) {
  return TOOL_STACKS.find(stack => stack.id === stackId);
}

export function getStackTools(stackId) {
  const stack = getToolStackById(stackId);
  if (!stack) return [];
  return stack.tools.map(toolId => getToolById(toolId)).filter(Boolean);
}

export function getOpportunitiesByCategory(category) {
  return OPPORTUNITY_DATABASE[category] || [];
}

// ============================================
// INTELLIGENT MATCHING FUNCTIONS (V1)
// ============================================

/**
 * Get AI tools that match a specific skill requirement
 * Uses SKILL_TOOL_MAPPING to connect skills to tool categories and functions
 * @param {string} skill - The skill to match
 * @returns {Array} - Array of matching tools sorted by relevance
 */
export function getToolsForSkill(skill) {
  const normalizedSkill = skill.toLowerCase();
  const mapping = SKILL_TOOL_MAPPING[normalizedSkill];
  
  if (!mapping) {
    // Fallback: search in tool descriptions and functions
    return AI_REGISTRY.filter(tool => 
      tool.description.toLowerCase().includes(normalizedSkill) ||
      tool.functions.some(f => f.toLowerCase().includes(normalizedSkill))
    ).slice(0, 5);
  }
  
  // Find tools that match the category and function requirements
  const matchingTools = AI_REGISTRY.filter(tool => {
    const categoryMatch = mapping.categories.includes(tool.category);
    const functionMatch = mapping.functions.some(f => tool.functions.includes(f));
    return categoryMatch || functionMatch;
  });
  
  // Sort by relevance score (reliability + workforce fit)
  return matchingTools.sort((a, b) => {
    const scoreA = (a.scores?.reliability || 0) + (a.scores?.workforceFit || 0);
    const scoreB = (b.scores?.reliability || 0) + (b.scores?.workforceFit || 0);
    return scoreB - scoreA;
  });
}

/**
 * Get recommended tools for an opportunity based on required skills
 * @param {Object} opportunity - The opportunity object
 * @returns {Array} - Array of recommended tools with match reasons
 */
export function getRecommendedToolsForOpportunity(opportunity) {
  const recommendedSet = new Map();
  
  // First, add explicitly recommended tools
  opportunity.recommendedTools?.forEach(toolId => {
    const tool = getToolById(toolId);
    if (tool) {
      recommendedSet.set(toolId, {
        tool,
        matchType: 'explicit',
        matchReason: 'Specifically recommended for this opportunity'
      });
    }
  });
  
  // Then, find tools based on required skills
  opportunity.requiredSkills?.forEach(skill => {
    const matchingTools = getToolsForSkill(skill);
    matchingTools.slice(0, 3).forEach(tool => {
      if (!recommendedSet.has(tool.id)) {
        recommendedSet.set(tool.id, {
          tool,
          matchType: 'skill-based',
          matchReason: `Matches skill requirement: ${skill}`
        });
      }
    });
  });
  
  return Array.from(recommendedSet.values());
}

/**
 * Check if a user's WAIT scores meet opportunity requirements
 * @param {Object} waitScores - User's WAIT scores
 * @param {Object} requirements - Opportunity's WAIT score requirements
 * @returns {Object} - Match result with details
 */
export function checkWaitScoreMatch(waitScores, requirements) {
  if (!waitScores || !requirements) {
    return { matches: true, gaps: [], score: 100 };
  }
  
  const gaps = [];
  let totalChecks = 0;
  let passedChecks = 0;
  
  if (requirements.minAiCapability && waitScores.aiCapability !== null) {
    totalChecks++;
    if (waitScores.aiCapability >= requirements.minAiCapability) {
      passedChecks++;
    } else {
      gaps.push({ dimension: 'AI Capability', required: requirements.minAiCapability, current: waitScores.aiCapability });
    }
  }
  
  if (requirements.minAlignment && waitScores.alignment !== null) {
    totalChecks++;
    if (waitScores.alignment >= requirements.minAlignment) {
      passedChecks++;
    } else {
      gaps.push({ dimension: 'Alignment', required: requirements.minAlignment, current: waitScores.alignment });
    }
  }
  
  if (requirements.minTrust && waitScores.trust !== null) {
    totalChecks++;
    if (waitScores.trust >= requirements.minTrust) {
      passedChecks++;
    } else {
      gaps.push({ dimension: 'Trust', required: requirements.minTrust, current: waitScores.trust });
    }
  }
  
  if (requirements.minReadiness && waitScores.readiness !== null) {
    totalChecks++;
    if (waitScores.readiness >= requirements.minReadiness) {
      passedChecks++;
    } else {
      gaps.push({ dimension: 'Readiness', required: requirements.minReadiness, current: waitScores.readiness });
    }
  }
  
  if (requirements.minResilience && waitScores.resilience !== null) {
    totalChecks++;
    if (waitScores.resilience >= requirements.minResilience) {
      passedChecks++;
    } else {
      gaps.push({ dimension: 'Resilience', required: requirements.minResilience, current: waitScores.resilience });
    }
  }
  
  const score = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 100;
  
  return {
    matches: gaps.length === 0,
    gaps,
    score,
    passedChecks,
    totalChecks
  };
}

/**
 * Get personalized opportunity recommendations based on user profile
 * @param {Object} userProfile - User's profile including WAIT scores, skills, preferences
 * @returns {Object} - Categorized and ranked opportunities
 */
export function getOpportunitiesForProfile(userProfile = {}) {
  const { waitScores, currentSkills = [], experienceLevel, industryFocus } = userProfile;
  
  const rankOpportunity = (opp) => {
    let score = 50; // Base score
    
    // WAIT Score matching (if available)
    if (waitScores && opp.waitScoreRequirements) {
      const waitMatch = checkWaitScoreMatch(waitScores, opp.waitScoreRequirements);
      score += waitMatch.score * 0.3;
    }
    
    // Skill matching
    if (currentSkills.length > 0 && opp.requiredSkills) {
      const skillMatches = opp.requiredSkills.filter(skill => 
        currentSkills.some(cs => cs.toLowerCase().includes(skill.toLowerCase()) || 
                                 skill.toLowerCase().includes(cs.toLowerCase()))
      ).length;
      score += (skillMatches / opp.requiredSkills.length) * 30;
    }
    
    // Difficulty matching
    if (experienceLevel && opp.difficultyLevel) {
      if (experienceLevel === opp.difficultyLevel) {
        score += 20;
      } else if (
        (experienceLevel === 'intermediate' && opp.difficultyLevel === 'beginner') ||
        (experienceLevel === 'advanced' && opp.difficultyLevel !== 'advanced')
      ) {
        score += 10;
      }
    }
    
    // Industry matching
    if (industryFocus && opp.industryFit) {
      if (opp.industryFit.includes("All Industries") || opp.industryFit.includes(industryFocus)) {
        score += 10;
      }
    }
    
    // Demand bonus
    if (opp.marketDemand === DEMAND_LEVEL.VERY_HIGH) score += 10;
    else if (opp.marketDemand === DEMAND_LEVEL.HIGH) score += 5;
    
    return score;
  };
  
  // Rank and return opportunities by category
  return {
    business: OPPORTUNITY_DATABASE.business
      .map(opp => ({ ...opp, matchScore: rankOpportunity(opp) }))
      .sort((a, b) => b.matchScore - a.matchScore),
    service: OPPORTUNITY_DATABASE.service
      .map(opp => ({ ...opp, matchScore: rankOpportunity(opp) }))
      .sort((a, b) => b.matchScore - a.matchScore),
    career: OPPORTUNITY_DATABASE.career
      .map(opp => ({ ...opp, matchScore: rankOpportunity(opp) }))
      .sort((a, b) => b.matchScore - a.matchScore)
  };
}

/**
 * Get opportunity by ID
 * @param {string} opportunityId 
 * @returns {Object|undefined}
 */
export function getOpportunityById(opportunityId) {
  const allOpportunities = [
    ...OPPORTUNITY_DATABASE.business,
    ...OPPORTUNITY_DATABASE.service,
    ...OPPORTUNITY_DATABASE.career
  ];
  return allOpportunities.find(opp => opp.id === opportunityId);
}

/**
 * Get all opportunities filtered by difficulty level
 * @param {string} level - beginner, intermediate, advanced
 * @returns {Object} - Opportunities organized by category
 */
export function getOpportunitiesByDifficulty(level) {
  return {
    business: OPPORTUNITY_DATABASE.business.filter(opp => opp.difficultyLevel === level),
    service: OPPORTUNITY_DATABASE.service.filter(opp => opp.difficultyLevel === level),
    career: OPPORTUNITY_DATABASE.career.filter(opp => opp.difficultyLevel === level)
  };
}

/**
 * Get total opportunity count
 * @returns {number}
 */
export function getTotalOpportunityCount() {
  return OPPORTUNITY_DATABASE.business.length + 
         OPPORTUNITY_DATABASE.service.length + 
         OPPORTUNITY_DATABASE.career.length;
}

// ============================================
// STATISTICS FUNCTIONS
// ============================================

export function getCategoryStats() {
  return AI_CATEGORIES.map(cat => ({
    ...cat,
    count: AI_REGISTRY.filter(tool => tool.category === cat.id).length
  }));
}

export function getLabStats() {
  return {
    workforce: getToolsByLab('workforce').length,
    organization: getToolsByLab('organization').length,
    institutional: getToolsByLab('institutional').length
  };
}

export function getRegistryStats() {
  return {
    totalTools: AI_REGISTRY.length,
    verifiedTools: getVerifiedTools().length,
    categories: AI_CATEGORIES.length,
    toolStacks: TOOL_STACKS.length,
    totalOpportunities: getTotalOpportunityCount(),
    averageReliability: Math.round(
      AI_REGISTRY.reduce((sum, tool) => sum + (tool.scores?.reliability || 0), 0) / AI_REGISTRY.length
    )
  };
}
